
import { Component, Prop, Vue } from 'vue-property-decorator';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { userRights } from '@/helpers/CmsShowHelper';
import { downloadCSV, escapeCsvValue } from '@/helpers/ProductVersionHelper';

@Component({
    name: 'UserRightsTable',
})
export default class UserRightsTable extends Vue {
    @Prop({ type: Array, required: false, default: () => [] }) readonly userGroups!: any[];

    private search: string = '';
    private userGroupId: string | null = null;
    private userRights = userRights;
    private innerUserGroupIds: Record<string, string | null> = {};

    private get columns() {
        const basicColumns = [
            {
                title: this.$t('Name'),
                dataIndex: 'name',
                key: 'name',
                sorter: (a: any, b: any) => a.name.localeCompare(b.name),
            },
            {
                title: this.$t('Usergroup'),
                dataIndex: 'usergroup',
                key: 'usergroup',
                scopedSlots: { customRender: 'userGroup' },
            },
        ];

        if (!this.users.length) {
            return basicColumns;
        }

        for (const userRightItem of this.userRights) {
            basicColumns.push({
                title: this.$t(userRightItem.label),
                dataIndex: userRightItem.name,
                key: userRightItem.name,
                sorter: (a: any, b: any) => {
                    const statusA = a[userRightItem.name] || '';
                    const statusB = b[userRightItem.name] || '';
                    return statusA.localeCompare(statusB);
                },
            });
        }

        return basicColumns;
    }

    private get myData() {
        return this.users
            .map((user) => {
                if (this.search && !user?.name.toLowerCase().includes(this.search.toLowerCase())) {
                    return null;
                }

                if (
                    this.userGroupId &&
                    user.userGroupConnections.every((connection: any) => connection.userGroup?.id !== this.userGroupId)
                ) {
                    return null;
                }

                const userData: any = {
                    name: user.name,
                    usergroup: this.selectedUserGroup?.name || 'No group',
                    key: user.id,
                };

                for (const userRightItem of this.userRights) {
                    userData[userRightItem.name] = this.determineUserRightStatus(user, userRightItem);
                }

                return userData;
            })
            .filter((userData) => userData !== null);
    }

    private get users() {
        return CMSUserRepository.getAllExceptArchivedUsers();
    }

    private get currentUserGroup() {
        return String(this.$store.getters['jwtData/currentUserGroup']);
    }

    private get selectedUserGroup() {
        return UserGroupRepository.getById(this.userGroupId || this.currentUserGroup);
    }

    private onUserGroupFilterChange(value: string) {
        this.innerUserGroupIds = {}; // Reset inner user group IDs
        this.users.forEach((user: any) => {
            this.$set(this.innerUserGroupIds, user.id, value); // Initialize with selected user group
        });
    }

    private onInnerUGFilterChange(user: any, value: string) {
        this.$set(this.innerUserGroupIds, user.key, value);
    }

    private determineUserRightStatus(user: any, userRightItem: { name: string; label: string }) {
        const userGroupConnection = user.userGroupConnections.find(
            (connection: any) => connection.userGroup?.id === (this.innerUserGroupIds[user.id] || this.currentUserGroup)
        );

        if (!userGroupConnection) {
            return 'No connection';
        }

        if (userGroupConnection.userRights) {
            // If it's an object, check for property existence or value
            // add icons
            return userGroupConnection.userRights[userRightItem.name] ? '✅' : '❌';
        }

        return 'No';
    }

    private filteredUserGroups(record: any) {
        return this.userGroups.filter((userGroup) => {
            const userGroupConnection = this.users.find((user: any) => user.id === record)?.userGroupConnections;
            return userGroupConnection?.some((connection: any) => connection.userGroup?.id === userGroup.id);
        });
    }

    private downloadExcel() {
        // Get the current displayed data and columns
        const tableData = this.myData;
        const tableColumns = this.columns;

        if (!tableData || tableData.length === 0) {
            return;
        }

        // Super simple direct approach - no intermediate variables, just build the CSV directly
        let csvRows: string[] = [];

        // 1. Create header row directly from column titles
        const headerRow = tableColumns
            .map((col) => {
                const title = col.title.toString();
                return escapeCsvValue(title);
            })
            .join(';');

        csvRows.push(headerRow);

        // 2. Create data rows directly from data
        tableData.forEach((row) => {
            const rowCells = tableColumns.map((column) => {
                const value = row[column.dataIndex] || '';
                const strValue = String(value);
                return escapeCsvValue(strValue);
            });

            csvRows.push(rowCells.join(';'));
        });

        // 3. Join all rows with newlines
        const csvContent = csvRows.join('\r\n');

        // 4. Generate filename with date and download
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
            today.getDate()
        ).padStart(2, '0')}`;
        const fileName = `user-rights-${dateStr}`;

        downloadCSV(csvContent, fileName);
    }

    private async mounted() {
        try {
            this.userGroupId = this.currentUserGroup || null;

            this.users.forEach((user: any) => {
                this.$set(this.innerUserGroupIds, user.id, this.currentUserGroup || null); // Initialize with current user group
            });
        } catch (error) {
            console.error('Error', error);
        }
    }
}
